
import React, { useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/use-auth';
import { 
  Home, 
  Users, 
  LayoutDashboard, 
  Settings, 
  BarChart3,
  FolderKanban,
  PieChart,
  Building2
} from 'lucide-react';

export interface AppSidebarProps {
  isMobileOpen: boolean;
  closeMobileSidebar: () => void;
}

export function AppSidebar({ isMobileOpen, closeMobileSidebar }: AppSidebarProps) {
  const location = useLocation();
  const { currentUser, isLoading, hasPermission } = useAuth();
  
  // Se cierra el sidebar móvil cuando se cambia de ruta
  React.useEffect(() => {
    if (isMobileOpen) {
      closeMobileSidebar();
    }
  }, [location.pathname, isMobileOpen, closeMobileSidebar]);

  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'superadmin';
  
  const menuItems = [
    {
      title: 'Dashboard',
      path: '/',
      icon: <Home className="w-5 h-5" />,
      permission: null, // No requiere permiso especial
    },
    {
      title: 'KPIs',
      path: '/kpis',
      icon: <BarChart3 className="w-5 h-5" />,
      permission: 'view_kpis',
    },
    {
      title: 'Departamentos',
      path: '/departments',
      icon: <FolderKanban className="w-5 h-5" />,
      permission: 'view_departments',
    },
    {
      title: 'Usuarios',
      path: '/users',
      icon: <Users className="w-5 h-5" />,
      permission: 'view_users',
    },
    {
      title: 'Reportes',
      path: '/reports',
      icon: <PieChart className="w-5 h-5" />,
      permission: 'view_reports',
    },
    {
      title: 'Configuración',
      path: '/settings',
      icon: <Settings className="w-5 h-5" />,
      permission: null,
    }
  ];

  const adminItems = [
    {
      title: 'Empresas',
      path: '/admin/companies',
      icon: <Building2 className="w-5 h-5" />,
    },
    {
      title: 'Solicitudes de acceso',
      path: '/admin/access-requests',
      icon: <Users className="w-5 h-5" />,
    }
  ];

  // Determina si una ruta está activa
  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <aside
      className={cn(
        "fixed inset-y-0 left-0 z-50 w-64 bg-background border-r border-border transition-transform duration-300 ease-in-out",
        "md:relative md:z-0 md:translate-x-0",
        isMobileOpen ? "translate-x-0" : "-translate-x-full"
      )}
    >
      <div className="flex flex-col h-full">
        <div className="flex items-center p-4 h-16 border-b">
          <span className="text-xl font-bold">KPI Ecosystem</span>
        </div>
        
        <nav className="flex-1 overflow-y-auto py-4 px-3">
          <ul className="space-y-2">
            {menuItems.map((item) => {
              // Si el ítem requiere un permiso y el usuario no lo tiene, no lo mostramos
              if (item.permission && !hasPermission(item.permission)) {
                return null;
              }
              
              return (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    className={cn(
                      "flex items-center p-2 rounded-md hover:bg-muted transition-colors",
                      isActive(item.path) && "bg-muted font-medium"
                    )}
                    onClick={closeMobileSidebar}
                  >
                    <span className="mr-3">{item.icon}</span>
                    <span>{item.title}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
          
          {isAdmin && (
            <div className="mt-6 pt-4 border-t border-border">
              <h3 className="px-2 mb-2 text-sm font-medium text-muted-foreground">
                Administración
              </h3>
              <ul className="space-y-2">
                {adminItems.map((item) => (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      className={cn(
                        "flex items-center p-2 rounded-md hover:bg-muted transition-colors",
                        isActive(item.path) && "bg-muted font-medium"
                      )}
                      onClick={closeMobileSidebar}
                    >
                      <span className="mr-3">{item.icon}</span>
                      <span>{item.title}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </nav>
        
        <div className="p-4 border-t">
          <div className="flex items-center">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-medium">
              {currentUser?.name ? currentUser.name.charAt(0).toUpperCase() : 'U'}
            </div>
            <div className="ml-3 overflow-hidden">
              <p className="text-sm font-medium truncate">
                {currentUser?.name || 'Usuario'}
              </p>
              <p className="text-xs text-muted-foreground truncate">
                {currentUser?.email || ''}
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Overlay para cerrar el sidebar en móvil */}
      {isMobileOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-[-1] md:hidden" 
          onClick={closeMobileSidebar}
        />
      )}
    </aside>
  );
}
